// useApiHealth.ts - v12.10.18 (#1859: tell the user's own internet outage apart from ours)
// v11.8.7.0
// Confirms and tracks API outages for the maintenance overlay.
//
// Flow: api.ts reports a "suspect" (503 / network failure) -> we ping /healthz once to CONFIRM
// (so a single transient blip never flashes the overlay). If /healthz is unreachable we enter
// "down" state and poll /healthz every few seconds; when it returns we recover.
//
// v11.8.7.0 (M1): recovery now does a single CONTROLLED RELOAD instead of only
// queryClient.invalidateQueries(). Reason: not every page fetches through React Query — some
// (e.g. MessagesPage) load data in a one-shot useEffect, so invalidateQueries() left them stuck
// on their outage error ("Messages could not be loaded") until a manual refresh. A reload after a
// CONFIRMED recovery guarantees every page — React Query or manual-fetch — comes back clean. The
// overlay is on screen throughout, so the reload reads as the promised automatic reconnect.
import { useEffect, useRef, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { onApiSuspect } from './apiHealth';
import { buildUrl } from './api';

const POLL_MS = 4000;

// v12.10.18 (#1859, Tushar 10/20): "it also comes when i don't have a wifi signal ... which is confusing. can we check if
// user has internet or not." The overlay said "offline for maintenance" for ANY failure to reach /healthz - including the
// user's own Wi-Fi being down. Now: the browser says it is offline, OR /healthz fails AND an INDEPENDENT reachability probe
// fails too -> it is the user's connection ("Looks like you're offline"). Only when the internet answers and our API does
// not is it maintenance. The probe is an image load from two public hosts (img-src https: is allowed by the T2 CSP;
// connect-src is not, so fetch() to another host would be refused and always look "offline").
const PROBE_HOSTS = ['https://www.google.com/favicon.ico', 'https://www.cloudflare.com/favicon.ico'];
const PROBE_MS = 4000;

function probeOne(url: string): Promise<boolean> {
  return new Promise(resolve => {
    const img = new Image();
    const t = window.setTimeout(() => { img.src = ''; resolve(false); }, PROBE_MS);
    img.onload = () => { window.clearTimeout(t); resolve(true); };
    img.onerror = () => { window.clearTimeout(t); resolve(false); };
    img.src = `${url}?vs=${Date.now()}`;
  });
}

/** true = this device can reach the internet (either public host answers). */
export async function internetReachable(): Promise<boolean> {
  if (typeof navigator !== 'undefined' && navigator.onLine === false) return false;
  const results = await Promise.all(PROBE_HOSTS.map(probeOne));
  return results.some(Boolean);
}

async function pingHealth(): Promise<boolean> {
  try {
    const res = await fetch(`${buildUrl('/healthz')}?t=${Date.now()}`, { cache: 'no-store' });
    return res.ok;
  } catch {
    return false; // network reject / CORS-less 503 from a stopped pool => treat as down
  }
}

export interface ApiHealthState {
  /** true while a confirmed API outage is being shown. */
  down: boolean;
  /** epoch ms when the outage was confirmed (drives "offline since"), or null when healthy. */
  since: number | null;
  /** v12.10.18 (#1859): true when the outage is the USER's connection, not our server. */
  offline: boolean;
}

export function useApiHealth(): ApiHealthState {
  const [down, setDown] = useState(false);
  const [since, setSince] = useState<number | null>(null);
  const [offline, setOffline] = useState(false);
  const queryClient = useQueryClient();
  const downRef = useRef(false);
  const timer = useRef<number | undefined>(undefined);

  useEffect(() => {
    let cancelled = false;

    const setDownState = (value: boolean) => {
      downRef.current = value;
      if (!cancelled) {
        setDown(value);
        setSince(value ? Date.now() : null);
        if (!value) setOffline(false);
      }
    };

    // #1859: which outage is it? Re-checked on every poll so the message switches by itself (Wi-Fi back, server still
    // deploying -> "We'll be right back"; and the other way round).
    async function classify() {
      const net = await internetReachable();
      if (!cancelled) setOffline(!net);
    }

    async function confirmOutage() {
      if (downRef.current) return;            // already showing the overlay
      const ok = await pingHealth();
      if (cancelled || ok) return;            // false alarm — ignore
      await classify();
      if (cancelled) return;
      setDownState(true);
      timer.current = window.setInterval(async () => {
        const recovered = await pingHealth();
        if (cancelled) return;
        if (!recovered) { void classify(); return; }
        window.clearInterval(timer.current);
        timer.current = undefined;
        // Invalidate first (best-effort for React Query pages), then do one controlled reload so
        // EVERY page — including manual-fetch pages — recovers cleanly. See the M1 note above.
        queryClient.invalidateQueries();
        window.location.reload();
      }, POLL_MS);
    }

    const unsubscribe = onApiSuspect(confirmOutage);
    // #1859: the browser's own online / offline events - the card shows (and switches) without waiting for a failed call.
    const onOffline = () => { void confirmOutage(); if (downRef.current) void classify(); };
    const onOnline = () => { if (downRef.current) void classify(); };
    window.addEventListener('offline', onOffline);
    window.addEventListener('online', onOnline);
    return () => {
      cancelled = true;
      unsubscribe();
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('online', onOnline);
      if (timer.current) window.clearInterval(timer.current);
    };
  }, [queryClient]);

  return { down, since, offline };
}
